import { useState } from 'react';

interface CoachFinderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CoachFinderModal({ isOpen, onClose }: CoachFinderModalProps) {
  const [formData, setFormData] = useState({
    playerName: '',
    email: '',
    skillLevel: '',
    gameMode: '',
    preferredSchedule: '',
    goals: '',
    budget: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const formDataToSubmit = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5bom3ndcu97hjrn3rc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setFormData({
            playerName: '',
            email: '',
            skillLevel: '',
            gameMode: '',
            preferredSchedule: '',
            goals: '',
            budget: '',
            additionalInfo: ''
          });
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
        setErrorMessage('Submission failed. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Network error. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Find Your Perfect Coach</h2>
            <p className="text-gray-400 text-sm mt-1">Tell us what you're looking for and we'll match you with the best coaches</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} data-readdy-form id="coach-finder-form" className="p-6 space-y-5">
          {submitStatus === 'success' && (
            <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg">
              <i className="ri-check-circle-fill mr-2"></i>
              Request submitted! We'll match you with suitable coaches and contact you soon.
            </div>
          )}

          {errorMessage && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg">
              <i className="ri-error-warning-fill mr-2"></i>
              {errorMessage}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="playerName" className="block text-sm font-medium text-gray-300 mb-2">
                Your Name *
              </label>
              <input
                type="text"
                id="playerName"
                name="playerName"
                value={formData.playerName}
                onChange={handleChange}
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="skillLevel" className="block text-sm font-medium text-gray-300 mb-2">
                Current Skill Level *
              </label>
              <select
                id="skillLevel"
                name="skillLevel"
                value={formData.skillLevel}
                onChange={handleChange}
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors pr-8 cursor-pointer"
              >
                <option value="">Select your level</option>
                <option value="Beginner">Beginner - Just starting</option>
                <option value="Intermediate">Intermediate - Regular player</option>
                <option value="Advanced">Advanced - Competitive</option>
                <option value="Professional">Professional - Tournament level</option>
              </select>
            </div>

            <div>
              <label htmlFor="gameMode" className="block text-sm font-medium text-gray-300 mb-2">
                Preferred Game Mode *
              </label>
              <select
                id="gameMode"
                name="gameMode"
                value={formData.gameMode}
                onChange={handleChange}
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors pr-8 cursor-pointer"
              >
                <option value="">Select game mode</option>
                <option value="1v1">1v1 Ranked</option>
                <option value="2v2">2v2 Team</option>
                <option value="Tournament">Tournament Prep</option>
                <option value="Casual">Casual Play</option>
                <option value="All">All Modes</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="preferredSchedule" className="block text-sm font-medium text-gray-300 mb-2">
                Preferred Schedule *
              </label>
              <select
                id="preferredSchedule"
                name="preferredSchedule"
                value={formData.preferredSchedule}
                onChange={handleChange}
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors pr-8 cursor-pointer"
              >
                <option value="">Select schedule</option>
                <option value="Weekday Mornings">Weekday Mornings</option>
                <option value="Weekday Afternoons">Weekday Afternoons</option>
                <option value="Weekday Evenings">Weekday Evenings</option>
                <option value="Weekends">Weekends</option>
                <option value="Flexible">Flexible Schedule</option>
              </select>
            </div>

            <div>
              <label htmlFor="budget" className="block text-sm font-medium text-gray-300 mb-2">
                Budget per Session *
              </label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors pr-8 cursor-pointer"
              >
                <option value="">Select budget range</option>
                <option value="$20-$40">$20 - $40 per hour</option>
                <option value="$40-$60">$40 - $60 per hour</option>
                <option value="$60-$100">$60 - $100 per hour</option>
                <option value="$100+">$100+ per hour</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="goals" className="block text-sm font-medium text-gray-300 mb-2">
              Training Goals *
            </label>
            <textarea
              id="goals"
              name="goals"
              value={formData.goals}
              onChange={handleChange}
              required
              maxLength={500}
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors resize-none"
              placeholder="What do you want to improve? (e.g., combo execution, defense, tournament preparation)"
            />
            <div className="text-right text-gray-500 text-xs mt-1">
              {formData.goals.length}/500
            </div>
          </div>

          <div>
            <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-300 mb-2">
              Additional Information
            </label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              maxLength={500}
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors resize-none"
              placeholder="Any specific requirements or preferences for your coach?"
            />
            <div className="text-right text-gray-500 text-xs mt-1">
              {formData.additionalInfo.length}/500
            </div>
          </div>

          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <div className="flex items-start">
              <i className="ri-information-line text-purple-400 text-xl mr-3 mt-0.5"></i>
              <div>
                <p className="text-white font-semibold mb-1">How It Works</p>
                <p className="text-gray-300 text-sm">
                  Our team will review your requirements and match you with 2-3 qualified coaches. You'll receive their profiles via email within 24 hours, then you can choose who to work with!
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Find My Coach'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
